<template>
    <div>

        <Head :title="form.part_number" />
        <h1 class="mb-8 text-3xl font-bold">
            <Link class="text-blue-400 hover:text-blue-600" href="/accessories">Accessories</Link>
            <span class="text-blue-400 font-medium">/</span>
            {{ form.part_number }}
        </h1>
        <trashed-message v-if="accessory.deleted_at" class="mb-6" @restore="restore"> This accessory has been deleted.
        </trashed-message>
        <div class="max-w-3xl bg-white shadow overflow-hidden">
            <form @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 p-8">
                    <select-input v-model="form.system" :error="form.errors.system" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="System">
                        <option v-for="s in systems" :key="s.id" :value="s.id"> {{ s.name }}</option>
                    </select-input>
                    <select-input v-model="form.width" :error="form.errors.width" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Width">
                        <option v-for="w in widths" :key="w.id" :value="w.id"> {{ w.in }}" ({{ w.mm }} mm)</option>
                    </select-input>
                    <select-input v-model="form.edge" :error="form.errors.edge" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Edge">
                        <option v-for="e in edges" :key="e.id" :value="e.id"> {{ e.name }}</option>
                    </select-input>
                    <select-input v-model="form.outlet_type" :error="form.errors.outlet_type"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Outlet Type">
                        <option v-for="o in outletTypes" :key="o.id" :value="o.id"> {{ o.name }}</option>
                    </select-input>
                    <text-input v-model="form.part_number" :error="form.errors.part_number"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Part Number" />
                    <text-input v-model="form.outlet_size" :error="form.errors.outlet_size"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Outlet Size (in)" type="number" step="0.1" />
                    <div class="pb-8 pr-6 w-full lg:w-1/2 flex">
                        <checkbox-input v-model="form.required" :error="form.errors.required" label="Required" />
                    </div>
                    <div class="pb-8 pr-6 w-full lg:w-1/2 flex">
                        <checkbox-input v-model="form.shallow" :error="form.errors.shallow" label="Shallow" />
                    </div>
                    <text-input v-model="form.per_channel" :error="form.errors.per_channel"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Per Channel" type="number" />
                    <text-input v-model="form.length_im" :error="form.errors.length_im"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Length Imperial (in)" type="number" step="0.01" />
                    <text-input v-model="form.length" :error="form.errors.length" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Length (mm)" type="number" />
                    <text-input v-model="form.depth_im" :error="form.errors.depth_im" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Depth Imperial (in)" type="number" step="0.01" />
                    <text-input v-model="form.depth" :error="form.errors.depth" class="pb-8 pr-6 w-full lg:w-1/2"
                        label="Depth (mm)" type="number" />
                    <text-input v-model="form.weight_kb" :error="form.errors.weight_kb"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Weight (kg)" type="number" step="0.001" />
                    <text-input v-model="form.weight_lbs" :error="form.errors.weight_lbs"
                        class="pb-8 pr-6 w-full lg:w-1/2" label="Weight (lbs)" type="number" step="0.001" />
                    <textarea-input v-model="form.description" :error="form.errors.description" class="pb-8 pr-6 w-full"
                        label="Description" />
                    
                    <!-- New Accessory Management Fields -->
                    <div class="pb-8 pr-6 w-full">
                        <h3 class="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Accessory Management</h3>
                        
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <!-- Is Optional Checkbox -->
                            <!-- <div class="flex items-center">
                                <checkbox-input v-model="form.is_optional" :error="form.errors.is_optional" 
                                    label="Optional Accessory" />
                                <span class="ml-2 text-sm text-gray-600">Check if this is an optional accessory</span>
                            </div> -->
                            
                            <!-- Groups Information (Read-only display) -->
                            <div v-if="form.is_optional">
                                <label class="form-label">Related Groups</label>
                                <div class="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                    <div class="flex flex-wrap gap-2">
                                        <span v-for="group in getAccessoryGroups()" :key="group"
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {{ group }}
                                        </span>
                                        <span v-if="getAccessoryGroups().length === 0" class="text-sm text-gray-500">
                                            No groups found for this part number
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Tooltip Field -->
                        <div class="mt-6">
                            <textarea-input v-model="form.tooltip" :error="form.errors.tooltip" 
                                class="w-full" label="Tooltip" rows="3"
                                placeholder="Enter tooltip text that will be shown to users when creating runs..." />
                        </div>
                        
                        <!-- Calculation Formula Field -->
                        <div class="mt-6" v-if="form.is_optional">
                            <text-input v-model="form.calculation_formula" :error="form.errors.calculation_formula"
                                class="w-full" label="Calculation Formula" 
                                placeholder="e.g., channels * 2, Math.ceil(length / 0.5)" />
                            <p class="mt-1 text-sm text-gray-600">
                                Optional formula for auto-calculating quantity. Available variables: channels, length
                            </p>
                        </div>
                    </div>
                    
                    <file-input v-model="form.image" :error="form.errors.image" class="pb-8 pr-6 w-full lg:w-1/2"
                        type="file" accept="image/*" label="Image" />
                </div>
                <!-- Price Lists -->
                <div class="mt-6 rounded shadow overflow-x-auto">
                    <table class="w-full whitespace-nowrap">
                        <tr class="text-left font-bold">
                            <th class="pb-4 pt-6 px-6">Price Lists</th>
                            <th class="pb-4 pt-6 px-6">Alias</th>
                            <th class="pb-4 pt-6 px-6">Price</th>
                        </tr>
                        <tr v-for="(pricelist, i) in form.pricelists" :key="pricelist.id"
                            class="hover:bg-gray-100 focus-within:bg-gray-100">
                            <td class="px-6 py-2 focus:text-blue-500">
                                {{ pricelist.name }}
                                <icon v-if="pricelist.deleted_at" name="trash"
                                    class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
                            </td>
                            <td class="px-6 py-2 focus:text-blue-500">
                                <text-input v-model="pricelist.alias" :error="form.errors[`pricelists.${i}.alias`]"
                                    class="w-full" />
                            </td>
                            <td class="px-6 py-2 focus:text-blue-500">
                                <text-input v-model="pricelist.price" :error="form.errors[`pricelists.${i}.price`]"
                                    class="w-full" label="" type="number" min="0" step="0.01" />
                            </td>
                        </tr>
                        <tr v-if="pricelists.length === 0">
                            <td class="px-6 py-4 border-t" colspan="4">No price lists found.</td>
                        </tr>
                    </table>
                </div>
                <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
                    <button v-if="!accessory.deleted_at" class="text-red-600 hover:underline" tabindex="-1"
                        type="button" @click="destroy">Delete Accessory</button>
                    <loading-button :loading="form.processing" class="btn-blue ml-auto" type="submit">Update
                        Accessory</loading-button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput'
import FileInput from '@/Shared/FileInput'
import CheckboxInput from '@/Shared/CheckboxInput'
import TrashedMessage from '@/Shared/TrashedMessage'

export default {
    components: {
        Head,
        Link,
        Icon,
        LoadingButton,
        TextInput,
        TextareaInput,
        SelectInput,
        FileInput,
        CheckboxInput,
        TrashedMessage,
    },
    layout: Layout,
    props: {
        systems: Array,
        widths: Array,
        edges: Array,
        outletTypes: Array,
        pricelists: Array,
        accessory: Object,
    },
    remember: 'form',
    data() {
        return {
            form: this.$inertia.form({
                _method: 'put',
                system: this.accessory.system,
                width: this.accessory.width,
                edge: this.accessory.edge,
                outlet_type: this.accessory.outlet_type,
                part_number: this.accessory.part_number,
                description: this.accessory.description,
                outlet_size: this.accessory.outlet_size,
                required: this.accessory.required,
                shallow: this.accessory.shallow,
                per_channel: this.accessory.per_channel,
                length_im: this.accessory.length_im,
                length: this.accessory.length,
                depth_im: this.accessory.depth_im,
                depth: this.accessory.depth,
                weight_kb: this.accessory.weight_kb,
                weight_lbs: this.accessory.weight_lbs,
                tooltip: this.accessory.tooltip,
                is_optional: this.accessory.is_optional,
                calculation_formula: this.accessory.calculation_formula,
                image: null,
                pricelists: this.pricelists,
            }),
        }
    },
    methods: {
        // Get groups that contain this accessory
        getAccessoryGroups() {
            if (!this.form.part_number) return [];
            
            // Import the series mapping from our service logic
            const seriesAccessoriesMap = {
                'T1400': [
                    'MI-4ADAPT', 'T1400-CI', 'T1400-PBDS',
                    'T1400-SIDE-ADAPT', 'T1410-SIDE-ADAPT', 'T1420-SIDE-ADAPT', 'T1400-SO4',
                    'T1400-S04', 'T1400-PMCC', 'T1400-SUSP', 'T1400-SHV',
                    'T100-KEY', 'MI-FLEX-300', 'MI-FLEX-400', 'T100-VP', 'T1400-SIKA'
                ],
                'T1460': [
                    'T1460-CI', 'T1460-REB', 'MI-4ADAPT', 'MI-FLEX-300', 'T1460-REB','T100-VP', 'T1400-SIKA', 'T1400-SUSP'
                ],
                'T2000': [
                    'MI-4ADAPT', 'T2000-CI',
                    'T1400-SIKA', 'T100-KEY', 'T2000-PBDS', 'MI-FLEX-600', 'MI-FLEX-400',
                    'T2000-SIDE-ADAPT', 'T2010-SIDE-ADAPT', 'T2020-SIDE-ADAPT',
                    'T2000-SO6', 'T1400-SHV', 'T200-VP', 'T2000-SUSP'
                ],
                'T2060': ['MI-4ADAPT', 'T1400-SIKA', 'T100-KEY', 'MI-FLEX-400', 'T2060-CI', 'T2060-REB'],
                'T3000': [
                    'T3000-CI', 'T3000-SIDE-ADAPT', 'T3000-PBDS', 'T3000-SO8',
                    'MI-FLEX-600', 'MI-FLEX-800', 'T3000-SUSP', 'T1400-SIKA',
                    'T3000-PEO8'
                ],
                'T1500': [
                    'T1500-PEO-BLK', 'T1500-PBDS', 'T1400-SHV', 'T100-KEY',
                    'MI-FLEX-300', 'MI-FLEX-400', 'T100-VP', 'T1400-SIKA',
                    'T1500-SUSP', 'T1500-CI', 'T1560-CI'
                ],
                'T1560': [
                    'T1400-SHV', 'T100-KEY',
                    'MI-FLEX-400', 'T1400-SIKA',
                    'T1500-SUSP', 'T1560-CI'
                ],
                'T1700': [
                    'T1700-PBDS', 'T1400-SHV', 'T100-KEY',
                    'MI-FLEX-400', 'MI-FLEX-600', 'T200-VP', 'T1400-SIKA',
                    'T1700-SUSP', 'T1700-CI'
                ],
                'T1760': [
                    'T1400-SHV', 'T100-KEY',
                    'MI-FLEX-400', 'MI-FLEX-600', 'T200-VP', 'T1400-SIKA',
                    'T1700-SUSP', 'T1760-CI'
                ],
                'T1800': [
                    'T1800-PBDS', 'T1400-SHV', 'T100-KEY',
                    'MI-FLEX-600', 'MI-FLEX-800', 'T1400-SIKA', 'T1800-SUSP',
                    'T1800-CI', 'T1860-CI'
                ],
                'P6060': [
                    'P6060-BO3-ST', 'P6060-BO4-ST'
                ],
                'P6080': [
                    'P6080-BO4-ST', 'P6080-BO6-ST', 'P6080-BO8-ST'
                ],
                'P6120': [
                    'P6120-BO6-ST', 'P6120-BO8-ST', 'P6120-BO12-ST'
                ]
            };
            
            const groups = [];
            for (const [series, accessories] of Object.entries(seriesAccessoriesMap)) {
                if (accessories.includes(this.form.part_number)) {
                    groups.push(series);
                }
            }
            return groups;
        },

        update() {
            this.form.post(`/accessories/${this.accessory.id}`, {
                onSuccess: () => this.form.reset('image'),
            })
        },
        destroy() {
            if (confirm('Are you sure you want to delete this accessory?')) {
                this.$inertia.delete(`/accessories/${this.accessory.id}`)
            }
        },
        restore() {
            if (confirm('Are you sure you want to restore this accessory?')) {
                this.$inertia.put(`/accessories/${this.accessory.id}/restore`)
            }
        },
    },
}
</script>